/**
 * Budget Alert Service
 * 
 * API service for budget alert management.
 */

import { apiClient } from '../client';

export interface BudgetAlertConfig {
  id: string;
  organization_id: string;
  budget_id: string;
  line_item_id?: string;
  alert_type: 'approaching' | 'exceeded';
  threshold_percent: number;
  email_enabled: boolean;
  notification_enabled: boolean;
  recipients?: string[];
  cooldown_hours: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  budget_name?: string;
  line_item_category?: string;
}

export interface BudgetLineItem {
  id: string;
  budget_id: string;
  organization_id: string;
  category: string;
  account_code?: string;
  budget_amount: number;
  spent_amount: number;
  description?: string;
  created_at: string;
  updated_at: string;
  budget_name?: string;
  fiscal_year?: number;
}

export interface BudgetAlertHistory {
  id: string;
  organization_id: string;
  budget_id: string;
  line_item_id?: string;
  alert_config_id?: string;
  alert_type: 'approaching' | 'exceeded';
  threshold_percent: number;
  actual_percent: number;
  budget_amount: number;
  spent_amount: number;
  message?: string;
  sent_via: 'email' | 'notification' | 'both';
  sent_to?: string[];
  status: 'sent' | 'delivered' | 'failed';
  error_message?: string;
  created_at: string;
  budget_name?: string;
  line_item_category?: string;
}

export interface BudgetStatus {
  id: string;
  name: string;
  fiscal_year: number;
  total_budget: number;
  allocated_amount: number;
  total_spent: number;
  spent_percent: number;
}

export interface CreateAlertConfigRequest {
  organization_id: string;
  budget_id: string;
  line_item_id?: string;
  alert_type: 'approaching' | 'exceeded';
  threshold_percent: number;
  email_enabled?: boolean;
  notification_enabled?: boolean;
  recipients?: string[];
  cooldown_hours?: number;
}

export interface UpdateAlertConfigRequest {
  organization_id: string;
  alert_type?: 'approaching' | 'exceeded';
  threshold_percent?: number;
  email_enabled?: boolean;
  notification_enabled?: boolean;
  recipients?: string[];
  cooldown_hours?: number;
  is_active?: boolean;
}

export class BudgetAlertService {
  /**
   * List alert configurations for an organization
   */
  async listConfigs(organizationId: string, budgetId?: string): Promise<BudgetAlertConfig[]> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (budgetId) params.append('budget_id', budgetId);
    
    const response = await apiClient.get<{ configs: BudgetAlertConfig[] }>(
      `/budget-alerts/configs?${params.toString()}`
    );
    return response.configs;
  }

  /**
   * Create a new alert configuration
   */
  async createConfig(config: CreateAlertConfigRequest): Promise<{ config_id: string }> {
    return apiClient.post<{ config_id: string }>('/budget-alerts/configs', config);
  }

  /**
   * Update an alert configuration
   */
  async updateConfig(configId: string, updates: UpdateAlertConfigRequest): Promise<void> {
    await apiClient.put(`/budget-alerts/configs/${configId}`, updates);
  }

  /**
   * Delete an alert configuration
   */
  async deleteConfig(configId: string, organizationId: string): Promise<void> {
    await apiClient.delete(`/budget-alerts/configs/${configId}?organization_id=${organizationId}`);
  }

  /**
   * List budget line items
   */
  async listLineItems(organizationId: string, budgetId?: string): Promise<BudgetLineItem[]> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (budgetId) params.append('budget_id', budgetId);
    
    const response = await apiClient.get<{ line_items: BudgetLineItem[] }>(
      `/budget-alerts/line-items?${params.toString()}`
    );
    return response.line_items;
  }

  /**
   * Create a budget line item
   */
  async createLineItem(item: {
    organization_id: string;
    budget_id: string;
    category: string;
    account_code?: string;
    budget_amount: number;
    description?: string;
  }): Promise<{ line_item_id: string }> {
    return apiClient.post<{ line_item_id: string }>('/budget-alerts/line-items', item);
  }

  /**
   * Update a budget line item
   */
  async updateLineItem(itemId: string, updates: {
    organization_id: string;
    category?: string;
    account_code?: string;
    budget_amount?: number;
    description?: string;
  }): Promise<void> {
    await apiClient.put(`/budget-alerts/line-items/${itemId}`, updates);
  }

  /**
   * Delete a budget line item
   */
  async deleteLineItem(itemId: string, organizationId: string): Promise<void> {
    await apiClient.delete(`/budget-alerts/line-items/${itemId}?organization_id=${organizationId}`);
  }

  /**
   * Get alert history
   */
  async getHistory(organizationId: string, options?: {
    budgetId?: string;
    limit?: number;
    offset?: number;
  }): Promise<{ history: BudgetAlertHistory[]; pagination: { limit: number; offset: number } }> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (options?.budgetId) params.append('budget_id', options.budgetId);
    if (options?.limit) params.append('limit', options.limit.toString());
    if (options?.offset) params.append('offset', options.offset.toString());
    
    return apiClient.get<{ history: BudgetAlertHistory[]; pagination: { limit: number; offset: number } }>(
      `/budget-alerts/history?${params.toString()}`
    );
  }

  /**
   * Get budget status with spending
   */
  async getBudgetStatus(organizationId: string, options?: {
    budgetId?: string;
    year?: number;
  }): Promise<{ budgets: BudgetStatus[]; line_items: any[]; year: number }> {
    const params = new URLSearchParams();
    params.append('organization_id', organizationId);
    if (options?.budgetId) params.append('budget_id', options.budgetId);
    if (options?.year) params.append('year', options.year.toString());
    
    return apiClient.get<{ budgets: BudgetStatus[]; line_items: any[]; year: number }>(
      `/budget-alerts/status?${params.toString()}`
    );
  }

  /**
   * Trigger manual budget alert check
   */
  async triggerCheck(organizationId: string, budgetId?: string): Promise<void> {
    await apiClient.post('/budget-alerts/check', {
      organization_id: organizationId,
      budget_id: budgetId,
    });
  }
}

// Export singleton instance
export const budgetAlertService = new BudgetAlertService();
